import { Dialog as ChakraDialog, Portal } from '@chakra-ui/react';
import type { ReactNode } from 'react';

const SIZE_MAX_WIDTH: Record<string, string> = {
  xs: '444px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px',
};

export interface BBDialogProps {
  children?: ReactNode;
  isOpen?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  title?: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  titleActions?: ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export const BBDialog = ({
  children,
  isOpen = false,
  isLoading = false,
  onClose,
  title,
  subtitle,
  actions,
  titleActions,
  size = 'xl',
}: BBDialogProps) => {
  const maxWidth = SIZE_MAX_WIDTH[size] ?? SIZE_MAX_WIDTH.xl;

  return (
    // No `size` on Root: our `size` prop is a WIDTH scale (SIZE_MAX_WIDTH), applied as maxWidth on
    // Content. Chakra's own size recipe also sets height — `cover` is `height: 100%`, which stretched
    // every short dialog (e.g. "Archive Contract?") to the full viewport, and maxHeight="90vh" can't
    // shrink a box that already fills it. Height is left to the content. See e2e/dialog-height.spec.ts.
    <ChakraDialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose?.();
        }
      }}
      closeOnEscape={false}
      closeOnInteractOutside={false}
    >
      <Portal>
        <ChakraDialog.Backdrop />
        <ChakraDialog.Positioner>
          <ChakraDialog.Content
            maxWidth={maxWidth}
            width="100%"
            maxHeight="90vh"
            bg="bg.panel"
            color="fg"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <ChakraDialog.Header padding="20px" flexShrink="0">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', gap: 20, width: '100%' }}
              >
                {(title || subtitle) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    {title && <h1 style={{ margin: 0, lineHeight: 1.2 }}>{title}</h1>}
                    {subtitle && <h5 style={{ margin: 0, lineHeight: 1.2 }}>{subtitle}</h5>}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  {titleActions}
                  {onClose && (
                    <button
                      type="button"
                      aria-label="close"
                      onClick={onClose}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 24,
                        lineHeight: 1,
                        padding: 4,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </ChakraDialog.Header>
            <ChakraDialog.Body
              padding="40px"
              borderTopWidth="1px"
              borderTopStyle="solid"
              borderColor="border"
              borderBottomWidth={actions ? '1px' : '0'}
              borderBottomStyle="solid"
              flex="1"
              overflowY="auto"
              minHeight="0"
            >
              {isLoading && (
                <div
                  style={{
                    margin: '-40px -40px 20px',
                    height: 4,
                    background: 'var(--chakra-colors-border)',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: '30%',
                      background: 'var(--chakra-colors-bb-blue)',
                    }}
                  />
                </div>
              )}
              {children}
            </ChakraDialog.Body>
            {actions && (
              <ChakraDialog.Footer padding="20px" flexShrink="0">
                {actions}
              </ChakraDialog.Footer>
            )}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    </ChakraDialog.Root>
  );
};
